/**
 * Which ticket types the signed-in user may create (#1544).
 *
 * Roles can switch "Fehler melden" (`bug`) and "Verbesserung vorschlagen"
 * (`feature`) off; `/roles/me/permissions` carries the aggregated answer. This
 * only decides what the UI offers — the BFF refuses a disabled type with 403
 * `ticket_type_disabled` regardless.
 *
 * While loading, and when the request fails, both types count as allowed: the
 * entries must not flicker away on every page load, and the BFF has the last
 * word. Loaded once per tab and shared through a module cache, since the
 * spotlight, the rail, "Meine Tickets" and the panel all ask in the same render.
 * No window manager here — the hook also runs in `StandaloneLayout`.
 *
 * With both types off the ticket area itself goes — Cortex "Tickets" and the
 * standalone `/tickets` (#1625). `any` is false only once loaded, so it is
 * safe to redirect on.
 */
import { useEffect, useMemo, useState } from 'react'
import { logError } from '@/lib/logger'
import {
  DEFAULT_ALLOWED_TICKET_TYPES,
  roleManagementService,
  type AllowedTicketTypes,
} from '@/services/role-management-service'

let cache: Promise<AllowedTicketTypes> | null = null

function loadAllowedTicketTypes(): Promise<AllowedTicketTypes> {
  if (!cache) {
    cache = roleManagementService
      .getMyPermissions()
      .then((permissions) => permissions.allowed_ticket_types ?? DEFAULT_ALLOWED_TICKET_TYPES)
      .catch((error) => {
        logError(error as Error, { context: 'useAllowedTicketTypes' })
        cache = null // a failure must not stick
        return DEFAULT_ALLOWED_TICKET_TYPES
      })
  }
  return cache
}

/** Drop on sign-out, otherwise the next user would see someone else's rights. */
export function clearAllowedTicketTypesCache(): void {
  cache = null
}

export function useAllowedTicketTypes(): AllowedTicketTypes & { any: boolean } {
  const [types, setTypes] = useState<AllowedTicketTypes>(DEFAULT_ALLOWED_TICKET_TYPES)

  useEffect(() => {
    let active = true
    void loadAllowedTicketTypes().then((loaded) => {
      if (active) setTypes(loaded)
    })
    return () => {
      active = false
    }
  }, [])

  // Stable between renders, so callers can depend on it (the panel's listeners).
  return useMemo(() => ({ ...types, any: types.bug || types.feature }), [types])
}
